import React, { useState } from 'react';
import useApiRequest from './useApiRequest.jsx';
import { domain } from './Domain.jsx';
import notify from './Nots.jsx';
import { motion } from 'framer-motion';
import {
    Lock, Eye, EyeOff, Shield, CheckCircle, AlertCircle,
    Key, Save, X, Zap, AlertTriangle
} from 'lucide-react';
import { saveToLocalStorage, getFromLocalStorage } from './EncryptDecrypt.jsx';

const ChangePassword = () => {
    const { postRequest } = useApiRequest();
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState({});
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [loading, setLoading] = useState(false);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.currentPassword.trim()) {
            newErrors.currentPassword = 'Current password is required';
        }

        if (!formData.newPassword.trim()) {
            newErrors.newPassword = 'New password is required';
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters';
        } else if (!/(?=.*[a-z])/.test(formData.newPassword)) {
            newErrors.newPassword = 'Password must contain at least one lowercase letter';
        } else if (!/(?=.*[A-Z])/.test(formData.newPassword)) {
            newErrors.newPassword = 'Password must contain at least one uppercase letter';
        } else if (!/(?=.*\d)/.test(formData.newPassword)) {
            newErrors.newPassword = 'Password must contain at least one number';
        } else if (!/(?=.*[@$!%*?&])/.test(formData.newPassword)) {
            newErrors.newPassword = 'Password must contain at least one special character';
        }

        if (!formData.confirmPassword.trim()) {
            newErrors.confirmPassword = 'Please confirm your new password';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const getPasswordStrength = (password) => {
        if (!password) return { score: 0, label: '', color: '' };

        let score = 0;
        if (password.length >= 8) score++;
        if (/(?=.*[a-z])/.test(password)) score++;
        if (/(?=.*[A-Z])/.test(password)) score++;
        if (/(?=.*\d)/.test(password)) score++;
        if (/(?=.*[@$!%*?&])/.test(password)) score++;

        if (score <= 1) return { score, label: 'Very Weak', color: 'bg-red-500' };
        if (score === 2) return { score, label: 'Weak', color: 'bg-orange-500' };
        if (score === 3) return { score, label: 'Fair', color: 'bg-yellow-500' };
        if (score === 4) return { score, label: 'Good', color: 'bg-blue-500' };
        return { score, label: 'Strong', color: 'bg-green-500' };
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        try {
            const formDatas = new FormData();
            formDatas.append("old_password", formData.currentPassword);
            formDatas.append("new_password", formData.newPassword);
            formDatas.append("confirm_password", formData.confirmPassword);

            const response = await postRequest(`${domain}/accounts/change-passwords`, formDatas, true);

            if (response.status !== 200) {
                notify(response.message, "error");
            } else {
                const authInfo = getFromLocalStorage("authInfo");
                notify("Password changed successfully!", "success");

                setFormData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });

                saveToLocalStorage("authInfo", { ...authInfo, accessToken: response.body.access_token, refreshToken: response.body.refresh_token });

                setErrors({});
            }
        } catch (err) {
            console.error('Error changing password:', err);
            notify("An error occurred. Please try again later.", "error");
        } finally {
            setLoading(false);
        }
    };

    const passwordStrength = getPasswordStrength(formData.newPassword);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full"
        >
            <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                    <Lock className="w-6 h-6 !text-white" />
                </div>
                <div>
                    <h3 className="text-2xl font-bold text-gray-800">Change Password</h3>
                    <p className="text-gray-600">Update your account password for enhanced security</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Current Password */}
                <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                        Current Password
                    </label>
                    <div className="relative">
                        <input
                            type={showPasswords.current ? "text" : "password"}
                            name="currentPassword"
                            value={formData.currentPassword}
                            placeholder="Enter your current password"
                            onChange={handleChange}
                            className={`w-full px-4 py-4 pr-12 border-2 rounded-xl focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all duration-200 text-lg ${errors.currentPassword ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                }`}
                            required
                        />
                        <button
                            type="button"
                            onClick={() => togglePasswordVisibility('current')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        >
                            {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>
                    {errors.currentPassword && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.currentPassword}
                        </p>
                    )}
                </div>

                {/* New Password */}
                <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                        New Password
                    </label>
                    <div className="relative">
                        <input
                            type={showPasswords.new ? "text" : "password"}
                            name="newPassword"
                            value={formData.newPassword}
                            placeholder="Enter your new password"
                            onChange={handleChange}
                            className={`w-full px-4 py-4 pr-12 border-2 rounded-xl focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all duration-200 text-lg ${errors.newPassword ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                }`}
                            required
                        />
                        <button
                            type="button"
                            onClick={() => togglePasswordVisibility('new')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        >
                            {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>

                    {/* Password Strength Indicator */}
                    {formData.newPassword && (
                        <div className="mt-3 p-4 bg-gray-50 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">Password Strength</span>
                                <span className={`text-sm font-semibold px-2 py-1 rounded-full ${passwordStrength.score <= 1 ? 'bg-red-100 text-red-700' :
                                        passwordStrength.score === 2 ? 'bg-orange-100 text-orange-700' :
                                            passwordStrength.score === 3 ? 'bg-yellow-100 text-yellow-700' :
                                                passwordStrength.score === 4 ? 'bg-blue-100 text-blue-700' :
                                                    'bg-green-100 text-green-700'
                                    }`}>
                                    {passwordStrength.label}
                                </span>
                            </div>
                            <div className="flex gap-1 mb-3">
                                {[1, 2, 3, 4, 5].map((level) => (
                                    <div
                                        key={level}
                                        className={`h-2 flex-1 rounded-full transition-all duration-300 ${level <= passwordStrength.score ? passwordStrength.color : 'bg-gray-200'
                                            }`}
                                    />
                                ))}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-600">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className={`w-4 h-4 ${formData.newPassword.length >= 8 ? 'text-green-500' : 'text-gray-300'}`} />
                                    At least 8 characters
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle className={`w-4 h-4 ${/(?=.*[a-z])/.test(formData.newPassword) ? 'text-green-500' : 'text-gray-300'}`} />
                                    Lowercase letter
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle className={`w-4 h-4 ${/(?=.*[A-Z])/.test(formData.newPassword) ? 'text-green-500' : 'text-gray-300'}`} />
                                    Uppercase letter
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle className={`w-4 h-4 ${/(?=.*\d)/.test(formData.newPassword) ? 'text-green-500' : 'text-gray-300'}`} />
                                    Number
                                </div>
                                <div className="flex items-center gap-2 md:col-span-2">
                                    <CheckCircle className={`w-4 h-4 ${/(?=.*[@$!%*?&])/.test(formData.newPassword) ? 'text-green-500' : 'text-gray-300'}`} />
                                    Special character (@$!%*?&)
                                </div>
                            </div>
                        </div>
                    )}

                    {errors.newPassword && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.newPassword}
                        </p>
                    )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                        Confirm New Password
                    </label>
                    <div className="relative">
                        <input
                            type={showPasswords.confirm ? "text" : "password"}
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            placeholder="Confirm your new password"
                            onChange={handleChange}
                            className={`w-full px-4 py-4 pr-12 border-2 rounded-xl focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all duration-200 text-lg ${errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                }`}
                            required
                        />
                        <button
                            type="button"
                            onClick={() => togglePasswordVisibility('confirm')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        >
                            {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>
                    {errors.confirmPassword && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.confirmPassword}
                        </p>
                    )}
                </div>

                {/* Security Tips */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <Shield className="w-4 h-4 text-blue-600" />
                        </div>
                        <h4 className="font-semibold text-gray-800">Password Security Tips</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-blue-500" />
                            Use a unique password for each account
                        </div>
                        <div className="flex items-center gap-2">
                            <Key className="w-4 h-4 text-blue-500" />
                            Avoid using personal information
                        </div>
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-blue-500" />
                            Never share your password with anyone
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-500" />
                            Consider using a password manager
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-6 border-t border-gray-200">
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 !text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                        {loading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Updating Password...
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                Update Password
                            </>
                        )}
                    </button>
                </div>
            </form>
        </motion.div>
    );
};

export default ChangePassword;
